/* eslint-disable guard-for-in */
import { BigNumber } from "@ethersproject/bignumber";
import type { TransactionResponse } from "@ethersproject/providers";
import { ChainId } from "@icpswap/constants";
import { SUPPORTED_CHAINS } from "constants/web3";
import { useAccount, useChainId } from "wagmi";
import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { ERC20Token } from "@icpswap/swap-sdk";
import { Null } from "@icpswap/types";

import { addTransaction, cancelTransaction, removeTransaction } from "./reducer";
import { TransactionDetails, TransactionInfo, TransactionType } from "./types";

// helper that can take a ethers library transaction response and add it to the list of transactions
export function useTransactionAdder(): (
  response: TransactionResponse,
  info: TransactionInfo,
  deadline?: number,
) => void {
  const chainId = useChainId();
  const { address: account } = useAccount();

  const dispatch = useAppDispatch();

  return useCallback(
    (response: TransactionResponse, info: TransactionInfo, deadline?: number) => {
      if (!account) return;
      if (!chainId) return;

      const { hash, nonce } = response;
      if (!hash) {
        throw Error("No transaction hash found.");
      }
      dispatch(addTransaction({ hash, from: account, info, chainId, nonce, deadline }));
    },
    [account, chainId, dispatch],
  );
}

export function useTransactionRemover() {
  const chainId = useChainId();
  const { address: account } = useAccount();
  const dispatch = useAppDispatch();

  return useCallback(
    (hash: string) => {
      if (!account) return;
      if (!chainId) return;

      dispatch(removeTransaction({ hash, chainId }));
    },
    [account, chainId, dispatch],
  );
}

export function useTransactionCanceller() {
  const dispatch = useAppDispatch();

  return useCallback(
    (hash: string, chainId: number, cancelHash: string) => {
      dispatch(cancelTransaction({ hash, chainId, cancelHash }));
    },
    [dispatch],
  );
}

export function useMultichainTransactions(): [TransactionDetails, ChainId][] {
  const state = useAppSelector((state) => state.transactions);
  return SUPPORTED_CHAINS.flatMap((chainId) =>
    state[chainId] ? Object.values(state[chainId]).map((tx): [TransactionDetails, ChainId] => [tx, chainId]) : [],
  );
}

// returns all the transactions for the current chain
function useAllTransactions(): { [txHash: string]: TransactionDetails } {
  const chainId = useChainId();
  const state = useAppSelector((state) => state.transactions);

  return chainId ? state[chainId] ?? {} : {};
}

export function useTransaction(transactionHash?: string): TransactionDetails | undefined {
  const allTransactions = useAllTransactions();

  if (!transactionHash) {
    return undefined;
  }

  return allTransactions[transactionHash];
}

export function isPendingTx(tx: TransactionDetails): boolean {
  return !tx.receipt && !tx.cancelled;
}

export function useIsTransactionPending(transactionHash?: string): boolean {
  const transactions = useAllTransactions();

  if (!transactionHash || !transactions[transactionHash]) return false;

  return isPendingTx(transactions[transactionHash]);
}

export function useIsTransactionConfirmed(transactionHash?: string): boolean {
  const transactions = useAllTransactions();

  if (!transactionHash || !transactions[transactionHash]) return false;

  return Boolean(transactions[transactionHash].receipt);
}

/**
 * Returns whether a transaction happened in the last day (86400 seconds * 1000 milliseconds / second)
 * @param tx to check for recency
 */
function isTransactionRecent(tx: TransactionDetails): boolean {
  return new Date().getTime() - tx.addedTime < 86_400_000;
}

function usePendingApprovalAmount(token: ERC20Token | Null, spender: string | Null): BigNumber | undefined {
  const allTransactions = useAllTransactions();
  return useMemo(() => {
    if (typeof token?.address !== "string" || typeof spender !== "string") {
      return undefined;
    }
    for (const txHash in allTransactions) {
      const tx = allTransactions[txHash];
      if (!tx || tx.receipt || tx.info.type !== TransactionType.APPROVAL) continue;
      if (tx.info.spender === spender && tx.info.tokenAddress === token.address && isTransactionRecent(tx)) {
        return BigNumber.from(tx.info.amount);
      }
    }
    return undefined;
  }, [allTransactions, spender, token?.address]);
}

// returns whether a token has a pending approval transaction
export function useHasPendingApproval(token?: ERC20Token | Null, spender?: string | Null): boolean {
  return usePendingApprovalAmount(token, spender)?.gt(0) ?? false;
}

export function useHasPendingRevocation(token?: ERC20Token, spender?: string): boolean {
  return usePendingApprovalAmount(token, spender)?.eq(0) ?? false;
}

export function usePendingTransactions(): TransactionDetails[] {
  const allTransactions = useAllTransactions();
  const { address: account } = useAccount();

  return useMemo(
    () => Object.values(allTransactions).filter((tx) => tx.from === account && isPendingTx(tx)),
    [account, allTransactions],
  );
}
